import React from 'react';
import { RING_ON } from '../surface';

/**
 * The studio shell pieces the Email Studio and APP NOTICES share (design_handoff_email_studio
 * §3–4, design_handoff_app_notices "Page layout" / "Field kit"): the white radius-28 section
 * card, the pill segmented control, the BEFORE IT GOES checks list, the rainbow-ring CTA. One
 * copy, used by both — restyle here, never per builder. The soft inputs live in `./ui`
 * (`SoftField`, `SoftText`).
 */
export const PILL = 'inline-flex items-center justify-center rounded-full font-mono font-extrabold uppercase transition-colors duration-200 focus:outline-none focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4px_#000] disabled:cursor-not-allowed';
export const pillOf = (on) => ({ background: on ? '#0A0A0A' : '#F2F2F2', color: on ? '#fff' : '#000' });
export const ICON = {
  ok: 'M5 12l5 5L20 7', no: 'M12 7v6M12 17h.01',
  back: 'M19 12H5M12 19l-7-7 7-7', up: 'M12 19V5M5 12l7-7 7 7', down: 'M12 5v14M19 12l-7 7-7-7',
  dup: 'M8 8h12v12H8zM4 16V4h12', del: 'M18 6L6 18M6 6l12 12', close: 'M18 6L6 18M6 6l12 12',
};

export function Icon({ d, size = 14, stroke = 2.5 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>;
}

export function SearchIcon({ size = 14 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" style={{ flexShrink: 0 }}><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></svg>;
}

/** A section: white, radius 28 (22 on a phone), padding 24 (16), title 12/800/.18em and a muted note on the right. */
export function StudioCard({ title, note, noteColor = '#9CA3AF', right, children, testId, gap = 18, narrow = false, innerRef, id, style }) {
  return (
    <section ref={innerRef} id={id} className="flex flex-col bg-white font-mono" style={{ borderRadius: narrow ? 22 : 28, padding: narrow ? 16 : 24, gap, ...style }} data-testid={testId}>
      {(title || note || right) && (
        <div className="flex flex-wrap items-baseline justify-between" style={{ gap: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.18em' }}>{title}</span>
          {right || (note && <span style={{ fontSize: 10, letterSpacing: '.12em', color: noteColor }}>{note}</span>)}
        </div>
      )}
      {children}
    </section>
  );
}

/** The pill segmented control: a track (`#F2F2F2` or white) of 32px pills, the current one black. */
export function Segmented({ label, options, value, onChange, readOnly, track = '#fff', height = 32, ariaLabel }) {
  return (
    <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
      {label && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: '#6B7280' }}>{label}</span>}
      <div className="flex flex-wrap" style={{ gap: 4, padding: 4, borderRadius: 999, background: track }} role="radiogroup" aria-label={ariaLabel || label}>
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={value === o.value} disabled={readOnly} onClick={() => onChange(o.value)}
            className="flex items-center focus:outline-none focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4px_#000]"
            style={{ height, padding: '0 14px', gap: 8, border: 'none', borderRadius: 999, ...pillOf(value === o.value), fontFamily: 'inherit', fontSize: 10, fontWeight: 800, letterSpacing: '.12em', cursor: readOnly ? 'default' : 'pointer' }}>
            {o.dot && <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: '50%', background: o.dot, boxShadow: '0 0 0 1px rgba(128,128,128,.5)' }} />}
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * BEFORE IT GOES: one 46px pill row per check — green ✓ on `#F7F7F7`, red ! on `#FEF2F2`. A
 * failing row with `onFix` is a button that jumps to what needs fixing.
 */
export function ChecksList({ checks, onFix, testIdPrefix = 'check' }) {
  return (
    <div className="flex flex-col" style={{ gap: 6 }} data-testid="send-problems">
      {checks.map((c) => {
        const Row = onFix ? 'button' : 'div';
        return (
          <Row key={`${c.key}-${c.label}`} type={onFix ? 'button' : undefined} disabled={onFix ? c.ok : undefined} onClick={onFix ? () => !c.ok && onFix(c) : undefined}
            data-testid={`${testIdPrefix}-${c.key}`} data-ok={c.ok ? 'true' : 'false'}
            className="flex items-center text-left disabled:cursor-default" style={{ gap: 12, minHeight: 46, padding: '6px 14px 6px 8px', border: 'none', borderRadius: 999, background: c.ok ? '#F7F7F7' : '#FEF2F2', fontSize: 12, fontFamily: 'inherit', color: '#000' }}>
            <span className="flex flex-none items-center justify-center" style={{ width: 30, height: 30, borderRadius: '50%', background: c.ok ? '#22C55E' : '#EF4444', color: '#fff' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={c.ok ? ICON.ok : ICON.no} /></svg>
            </span>
            <span style={{ flex: 1 }}>{c.label}</span>
            {!c.ok && onFix && <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.14em', color: '#6B7280' }}>FIX →</span>}
          </Row>
        );
      })}
    </div>
  );
}

/** The 60px finish button: black with the rainbow ring when `ready`, grey and not-allowed when not. */
export function RingCta({ ready, onClick, children, testId, style }) {
  return (
    <button type="button" disabled={!ready} onClick={onClick} data-testid={testId} className={PILL}
      style={{ height: 60, border: '2px solid transparent', fontSize: 13, letterSpacing: '.2em', color: '#fff', background: ready ? RING_ON : '#8A8A8A', cursor: ready ? 'pointer' : 'not-allowed', ...style }}>
      {children}
    </button>
  );
}

/** A 46px pill button in the studio: black (`solid`), grey (`plain`) or the soft red (`danger`). */
export function StudioButton({ variant = 'plain', children, style, className = '', ...rest }) {
  const look = variant === 'solid' ? { background: '#0A0A0A', color: '#fff' } : variant === 'danger' ? { background: '#FEE2E2', color: '#B91C1C' } : variant === 'white' ? { background: '#fff', color: '#000' } : { background: '#F2F2F2', color: '#000' };
  return <button type="button" className={`${PILL} hover:opacity-85 ${className}`} style={{ height: 46, padding: '0 20px', fontSize: 11, letterSpacing: '.14em', ...look, ...style }} {...rest}>{children}</button>;
}
